import { BasketPropertyBag } from '@epicuro-next/data/baskets-dto';
import { createAction, props } from '@ngrx/store';
import {
  AlternativeAddress,
  BasketAccountDelivery,
  CheckOrderResult,
  Delivery,
  Maybe,
  ValidationResult,
} from 'graphql-api/baskets-api-interfaces';

/**
 * Action to set a basket id as active
 */
export const setActiveBasketId = createAction(
  '[Basket] Set Active Basket Id',
  props<{ basketId: string }>(),
);
/**
 * Check set a basket id action
 */
export const setActiveBasketIdFailure = createAction(
  '[Basket] Set Active Basket Id Failure',
  props<{ error: string }>(),
);

/**
 * Action to save the basketId as last used on BE for active account
 */
export const updateLastUsedBasketId = createAction(
  '[Basket] Update Last Used Basket Id',
  props<{ basketId: string }>(),
);

export const validateBasket = createAction('[Basket] Validate basket');

export const runBasketValidations = createAction(
  '[Basket] Run basket validations',
);
export const runBasketValidationsSuccess = createAction(
  '[Basket] Run basket validations success',
  props<{
    validationResults: ValidationResult[];
  }>(),
);

export const clearBasketValidations = createAction(
  '[Basket] Clear Basket Validations',
);

/**
 * Action to check the basket
 */
export const checkBasket = createAction('[Basket] Check basket');
/**
 * Check basket success action
 */
export const checkBasketSuccess = createAction(
  '[Basket] Check basket Success',
  props<{ validationResults: CheckOrderResult }>(),
);
/**
 * Check basket failure action
 */
export const checkBasketFailure = createAction(
  '[Basket] Check basket Failure',
  props<{ error: string }>(),
);

export const submitBasket = createAction('[Basket] Submit Basket');

export const submitBasketSuccess = createAction(
  '[Basket] Submit Basket Success',
  props<{ basketId: string }>(),
);

export const submitBasketFailure = createAction(
  '[Basket] Submit Basket Failure',
  props<{ error: string }>(),
);

export const resetBasket = createAction('[Basket] Reset Basket');

export const resetBasketCheckedStatus = createAction(
  '[Basket] Reset Basket Checked Status',
  props<{ basketId: string }>(),
);

export const resetOpenedBasket = createAction('[Basket] Reset Opened Basket');

export const updateCustomOrderReference = createAction(
  '[Basket] Update Custom Order Reference',
  props<{ customOrderReference: string }>(),
);

export const updateCustomOrderReferenceSuccess = createAction(
  '[Basket] Update Custom Order Reference Success',
  props<{ basketId: string; customOrderReference: string }>(),
);

export const updateCustomOrderReferenceFailure = createAction(
  '[Basket] Update Custom Order Reference Failure',
  props<{ error: string }>(),
);

export const updatePropertyBag = createAction(
  '[Basket] Update Property Bag',
  props<{
    propertyBag: BasketPropertyBag;
  }>(),
);

export const updatePropertyBagSuccess = createAction(
  '[Basket] Update Property Bag Success',
);

export const updatePropertyBagFailure = createAction(
  '[Basket] Update Property Bag Failure',
  props<{ propertyBag: BasketPropertyBag }>(),
);

export const updateAccountDeliveryClientReferenceNumber = createAction(
  '[Basket] Update Account Delivery Custom Order Reference',
  props<BasketAccountDelivery>(),
);

export const updateAccountDeliveryClientReferenceNumberSuccess = createAction(
  '[Basket] Update Account Delivery Custom Order Reference Success',
  props<BasketAccountDelivery>(),
);

export const updateAccountDeliveryClientReferenceNumberFailure = createAction(
  '[Basket] Update Account Delivery Custom Order Reference Failure',
  props<BasketAccountDelivery & { error: string }>(),
);

export const updateDeliveryLineClientReferenceNumber = createAction(
  '[Basket] Update Delivery Line Custom Order Reference',
  props<Delivery>(),
);

export const updateDeliveryLineClientReferenceNumberSuccess = createAction(
  '[Basket] Update Delivery Line Custom Order Reference Success',
  props<Delivery>(),
);

export const updateDeliveryLineClientReferenceNumberFailure = createAction(
  '[Basket] Update Delivery Line Custom Order Reference Failure',
  props<Delivery & { error: string }>(),
);

export const setBasketOperableStatus = createAction(
  '[Basket] Set Operable Status',
  props<{ isOperable: boolean }>(),
);

export const switchBasket = createAction(
  '[Basket] Switch Basket',
  props<{ basketId: string }>(),
);

export const switchBasketSuccess = createAction(
  '[Basket] Switch Basket Success',
  props<{ basketId: string }>(),
);

export const switchBasketFailure = createAction(
  '[Basket] Switch Basket Failure',
  props<{ message: string; status: number }>(),
);

export const deleteBasket = createAction(
  '[Basket] Delete Basket',
  props<{ basketId: string }>(),
);

export const deleteBasketSuccess = createAction(
  '[Basket] Delete Basket Success',
  props<{ basketId: string }>(),
);

export const deleteBasketFailure = createAction(
  '[Basket] Delete Basket Failure',
);

export const updateSelectedShippingOption = createAction(
  '[Basket] Update Selected Shipping Option',
  props<{ selectedShippingOptionId: string }>(),
);

export const updateSelectedShippingOptionSuccess = createAction(
  '[Basket] Update Selected Shipping Option Success',
  props<{ selectedShippingOptionId: string }>(),
);

export const updateSelectedShippingOptionFailed = createAction(
  '[Basket] Update Selected Shipping Option Fail',
  props<{ error: string }>(),
);

export const updatePreferredShippingOption = createAction(
  '[Basket] Update Preferred Shipping Option',
  props<{ preferredShippingOptionId: string }>(),
);

export const updateShipInFullOption = createAction(
  '[Basket] Update Ship In Full Option',
  props<{ basketId: string; userSpecifiedGroupedDelivery: boolean }>(),
);

export const updateShipInFullOptionSuccess = createAction(
  '[Basket] Update Ship In Full Option Success',
  props<{ basketId: string; userSpecifiedGroupedDelivery: boolean }>(),
);

export const updateShipInFullOptionFailed = createAction(
  '[Basket] Update Ship In Full Option Fail',
  props<{ basketId: string; error: string }>(),
);

export const setDropShip = createAction(
  '[Basket] Set Drop Ship',
  props<{ payload: Maybe<AlternativeAddress> }>(),
);

export const setDropShipSuccess = createAction(
  '[Basket] Set Drop Ship Success',
);

export const setDropShipFailure = createAction(
  '[Basket] Set Drop Ship Failure',
  props<{ error: string }>(),
);

export const submitForReview = createAction(
  '[Basket] Submit For Review',
  props<{ payload: { reviewerId: string; message: string } }>(),
);

export const submitForReviewSuccess = createAction(
  '[Basket] Submit For Review Success',
);

export const submitForReviewFailure = createAction(
  '[Basket] Submit For Review Failure',
  props<{ error: string }>(),
);

export const rejectBasket = createAction(
  '[Basket] Reject Basket',
  props<{ payload: { message: string } }>(),
);

export const rejectBasketSuccess = createAction(
  '[Basket] Reject Basket Success',
);

export const rejectBasketFailure = createAction(
  '[Basket] Reject Basket Failure',
  props<{ error: string }>(),
);

export const makeBasketActive = createAction(
  '[Basket] Make Basket Active',
  props<{ basketId: string }>(),
);

export const manualDiscountsBusyUpdating = createAction(
  '[Basket] Manual Discounts Busy Updating',
  props<{
    basketId: string;
  }>(),
);

export const manualDiscountsUpdatedSuccess = createAction(
  '[Basket] Manual Discounts Updated Success',
  props<{
    basketId: string;
  }>(),
);

export const manualDiscountsUpdatedFailed = createAction(
  '[Basket] Basket Manual Discounts Updated Failed',
  props<{ basketId: string; basketItemIds: string[]; message: string }>(),
);

export const manualDiscountsBusyDeleting = createAction(
  '[Basket] Manual Discounts Busy Deleting',
  props<{
    basketId: string;
  }>(),
);

export const manualDiscountsDeleteSuccess = createAction(
  '[Basket] Manual Discounts Delete Success',
  props<{ basketId: string }>(),
);

export const manualDiscountsDeleteFailed = createAction(
  '[Basket] Basket Manual Discounts Delete Failed',
  props<{
    basketId: string;
    basketItemIds: string[];
    message: string;
  }>(),
);

export const updateBasketEtag = createAction(
  '[Basket] Update Basket Etag',
  props<{ basketId: string; etag: string }>(),
);
